import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

/**
 * Custom validator for country-specific zip code validation
 * @param countryCode - The country code (codeA3) or country name
 * @param getZipPattern - Function to get the zip pattern for a country
 * @returns ValidatorFn
 */
export function zipCodeValidator(
  countryCode: string | (() => string),
  testZipCode: (zipCode: string, country: string) => boolean
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    // If no value, let the required validator handle it
    if (!value) {
      return null;
    }

    // Get the country code (either from string or function)
    const country = typeof countryCode === 'function' ? countryCode() : countryCode;

    if (!country) {
      return null;
    }

    const isValid = testZipCode(value, country);

    return isValid ? null : { invalidZipCode: { value, country } };
  };
}

/**
 * Dynamic zip code validator that updates when country changes
 * Use this when the country is part of the same form group
 */
export function dynamicZipCodeValidator(testZipCode: (zipCode: string, country: string) => boolean): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    // If no value, let the required validator handle it
    if (!value) {
      return null;
    }

    // Get the country from the parent form group
    const parent = control.parent;
    if (!parent) {
      return null;
    }

    const isSameAddressControl = parent.get('isSameAddress');
    if (isSameAddressControl && isSameAddressControl.value === true) {
      return null;
    }

    const countryControl = parent.get('country');
    const country = countryControl?.value;

    if (!country) {
      // If the country control is explicitly failing a required validator,
      // let that validator surface the error instead of adding a zip error.
      if (countryControl && countryControl.errors && countryControl.errors['required']) {
        return null;
      }

      // Otherwise, a non-empty zip code with no country should not silently pass validation.
      return { invalidZipCode: { value, country } };
    }

    const isValid = testZipCode(value, country);

    return isValid ? null : { invalidZipCode: { value, country } };
  };
}
